import * as Highcharts from 'highcharts';
import { FAR_TOOLTIP_DEFAULT_DISTANCE_PX } from './far-tooltip-suppression.constants';
import { FarTooltipChart } from './far-tooltip-suppression.types';

function isNearRenderedLine(
  chart: FarTooltipChart,
  pointerEvent: Highcharts.PointerEventObject
): boolean {
  const distancePx =
    chart.farTooltipDistancePx ?? FAR_TOOLTIP_DEFAULT_DISTANCE_PX;

  return chart.series.some((series) => {
    const options = series.options as Highcharts.SeriesLineOptions;
    if (
      !series.visible ||
      options.enableMouseTracking === false ||
      (series.type !== 'line' && series.type !== 'spline')
    ) {
      return false;
    }
    const point = series.searchPoint(pointerEvent, true);
    return (
      !!point &&
      point.plotY != null &&
      Math.abs(point.plotY + chart.plotTop - pointerEvent.chartY) <= distancePx
    );
  });
}

let farTooltipSuppressionInstalled = false;

/**
 * Shared tooltips snap to the nearest x regardless of vertical distance to
 * any line, so without this the tooltip box appears anywhere in the plot
 * area. Wrapping `runPointActions` — the method Highcharts' own mousemove
 * handler calls to run hover point actions and show the tooltip — lets it
 * proceed as normal every time (so the per-series hover halos/markers keep
 * appearing at the shared x exactly as before), then immediately hides just
 * the tooltip box (with a zero-delay `hide(0)`, bypassing the `hideDelay`
 * fade so there's no lingering flash) when the cursor isn't actually near a
 * rendered line.
 */
export function installFarTooltipSuppression(): void {
  if (farTooltipSuppressionInstalled) return;
  farTooltipSuppressionInstalled = true;

  Highcharts.wrap(
    Highcharts.Pointer.prototype,
    'runPointActions',
    function (
      this: Highcharts.Pointer & { chart: FarTooltipChart },
      proceed: (
        e?: Highcharts.PointerEventObject,
        p?: Highcharts.Point,
        force?: boolean
      ) => void,
      e?: Highcharts.PointerEventObject,
      p?: Highcharts.Point,
      force?: boolean
    ) {
      proceed.call(this, e, p, force);

      const chart = this.chart;
      if (force || !chart.suppressFarTooltip || !e) return;
      if (!isNearRenderedLine(chart, e)) {
        chart.tooltip?.hide(0);
      }
    }
  );
}
